import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Alert,
    AppBar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Stack,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";

import {
    alpha,
} from "@mui/material/styles";

import {
    PRIMARY_COLOR,
} from "../util/constants";

import {
    toEmptyStringIfNull,
} from "../util/utilities";

import {
    fetchCategoriesAPI,
} from "../api/constants_api";

import {
    SALE_MODE,
    SALE_TYPE,
    SaleCreationRequest,
    createSaleAPI,
    fetchSaleTagsAPI,
} from "../api/sale_api";
import {
    validateNonEmptyAlphanumericString,
    validateNonEmptyAlphanumericText as validatePossiblyEmptyAlphanumericText,
    validateNonEmptyEmailAddress,
    validateNonEmptyHouseNo,
    validateNonEmptyPhoneNumber,
    validateNonEmptyZipCode,
} from "../api/validation_api";

import {
    VALIDATION,
} from "../util/configuration";

const LABEL = Object.freeze({
    title: "Lead hinzufügen",
    closeDialog: "Dialog schließen",
    offerSectionTitle: "Angebot",
    offerSectionSubtitle: "Produkt, Preis und Partnergruppe",
    companySectionTitle: "Unternehmen",
    personSectionTitle: "Person",
    contactSectionSubtitle: "Stammdaten",
    businessField: "Unternehmen",
    publicCommentSectionTitle: "Öffentlicher Kommentar",
    privateCommentSectionTitle: "Privater Kommentar",
    optional: "(optional)",
    save: "Speichern",
    saving: "Wird gespeichert …",
    loading: "Formulardaten werden geladen …",
    category: "Lead-Kategorie",
    saleType: "Kundentyp",
    b2b: "Geschäftskunde (B2B)",
    b2c: "Privatkunde (B2C)",
    mode: "Verkaufsmodus",
    offer: "Sofortkauf",
    auction: "Auktion",
    price: "Preis in €",
    minimumBid: "Mindestgebot in €",
    tags: "Partnergruppen",
    publicSale: "Öffentlicher Verkauf",
    tagsHelp:
        "Ohne Partnergruppe ist der Verkauf öffentlich. Mit Partnergruppen wird er privat.",
    noTags: "Keine Partnergruppen verfügbar",
    noCategories: "Keine Lead-Arten verfügbar",
    businessName: "Firma",
    firstName: "Vorname",
    lastName: "Nachname",
    birthDate: "Geburtsdatum",
    phoneNumber: "Telefonnummer",
    emailAddress: "E-Mail-Adresse",
    street: "Straße",
    houseNo: "Hausnummer",
    zipCode: "Postleitzahl",
    town: "Ort",
    country: "Land",
    publicComment: "Öffentlicher Kommentar (optional)",
    privateComment: "Interner Kommentar (optional)",
    offerData: "Allgemeine Angebotsdaten",
    contactData: "Kontaktdaten",
    comments: "Kommentare",
    invalidForm: "Bitte korrigieren Sie die markierten Eingaben.",
    requiredField: "Dieses Feld ist erforderlich.",
    invalidField: "Bitte geben Sie einen gültigen Wert ein.",
    accessTokenMissing: "Access Token fehlt.",
    categoryLoadError: "Die Lead-Arten konnten nicht geladen werden.",
    tagLoadError: "Die Partnergruppen konnten nicht geladen werden.",
    validationError: "Die Eingabe konnte nicht validiert werden.",
    saveError: "Der Lead konnte nicht gespeichert werden.",
    saved: "Lead wurde gespeichert.",
    visibilityWarning:
        "Öffentliche Angaben können vor dem Kaufabschluss für berechtigte Käufer sichtbar sein. Kontakt- und vollständige Adressdaten werden geschützt behandelt.",
});


const EMPTY_STRING = "";

const FIELD = Object.freeze({
    categoryId: "categoryId",
    saleType: "saleType",
    mode: "mode",
    price: "price",
    selectedTagIds: "selectedTagIds",
    businessName: "businessName",
    firstName: "firstName",
    lastName: "lastName",
    birthDate: "birthDate",
    phoneNumber: "phoneNumber",
    emailAddress: "emailAddress",
    street: "street",
    houseNo: "houseNo",
    zipCode: "zipCode",
    town: "town",
    publicComment: "publicComment",
    privateComment: "privateComment",
});

const COMPONENT_VALUE = Object.freeze({
    relative: "relative",
    inherit: "inherit",
    start: "start",
    h6: "h6",
    div: "div",
    span: "span",
    body2: "body2",
    textSecondary: "text.secondary",
    outlined: "outlined",
    error: "error",
    warning: "warning",
    small: "small",
    center: "center",
    flexStart: "flex-start",
    nowrap: "nowrap",
});

const INPUT_TYPE = Object.freeze({
    tel: "tel",
    email: "email",
    date: "date",
});

const AUTO_COMPLETE = Object.freeze({
    organization: "organization",
    givenName: "given-name",
    familyName: "family-name",
    tel: "tel",
    email: "email",
    streetAddress: "street-address",
    postalCode: "postal-code",
    addressLevel2: "address-level2",
});

const INPUT_PROPS = Object.freeze({
    numeric: Object.freeze({ inputMode: "numeric" }),
    tel: Object.freeze({ inputMode: "tel" }),
});

const INPUT_LABEL_PROPS = Object.freeze({
    shrink: true,
});

const OPTION_ID = Object.freeze({
    noCategories: "no-categories",
    noTags: "no-tags",
});

const STATUS_ID = Object.freeze({
    category: "category-load-error",
    tags: "tag-load-error",
});

const MIN_PRICE = 1;
const COUNTRY_GERMANY = "Deutschland";
const INTEGER_REGEX = /^[0-9]*$/;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const DIALOG_Z_INDEX = 4000;
const SELECT_MENU_Z_INDEX = DIALOG_Z_INDEX + 100;

const SELECT_MENU_PROPS = {
    sx: {
        zIndex: SELECT_MENU_Z_INDEX,
    },
    PaperProps: {
        sx: {
            maxHeight: 360,
        },
    },
};

const FORM_GRID_PROPS = Object.freeze({
    columnSpacing: {
        xs: 0,
        sm: 2.5,
        md: 3,
    },
    rowSpacing: {
        xs: 2.25,
        sm: 2.75,
    },
});

const FORM_FIELD_SX = Object.freeze({
    width: "100%",
    "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        backgroundColor: "background.paper",
        transition: "box-shadow 140ms ease, background-color 140ms ease",
        "& fieldset": {
            borderColor: "rgba(15, 23, 42, 0.18)",
        },
        "&:hover fieldset": {
            borderColor: "rgba(15, 23, 42, 0.34)",
        },
        "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(
                PRIMARY_COLOR,
                0.10,
            )}`,
        },
        "&.Mui-focused fieldset": {
            borderColor: PRIMARY_COLOR,
            borderWidth: 1,
        },
        "&.Mui-disabled": {
            backgroundColor: "#f8fafc",
        },
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: PRIMARY_COLOR,
    },
    "& .MuiFormHelperText-root": {
        lineHeight: 1.4,
    },
});

const INITIAL_VALUES = Object.freeze({
    categoryId: "",
    saleType: SALE_TYPE.B2C,
    mode: SALE_MODE.AUCTION,
    price: String(MIN_PRICE),
    selectedTagIds: [],
    businessName: "",
    firstName: "",
    lastName: "",
    birthDate: "",
    phoneNumber: "",
    emailAddress: "",
    street: "",
    houseNo: "",
    zipCode: "",
    town: "",
    publicComment: "",
    privateComment: "",
});

const VALIDATED_FIELDS = Object.freeze([
    "businessName",
    "firstName",
    "lastName",
    "birthDate",
    "phoneNumber",
    "emailAddress",
    "street",
    "houseNo",
    "zipCode",
    "town",
    "publicComment",
    "privateComment",
]);

const REQUIRED_FIELDS = Object.freeze([
    "firstName",
    "lastName",
    "phoneNumber",
    "emailAddress",
    "street",
    "houseNo",
    "zipCode",
    "town",
]);

const REMOTE_VALIDATORS = Object.freeze({
    businessName: validateNonEmptyAlphanumericString,
    firstName: validateNonEmptyAlphanumericString,
    lastName: validateNonEmptyAlphanumericString,
    phoneNumber: validateNonEmptyPhoneNumber,
    emailAddress: validateNonEmptyEmailAddress,
    street: validateNonEmptyAlphanumericString,
    houseNo: validateNonEmptyHouseNo,
    zipCode: validateNonEmptyZipCode,
    town: validateNonEmptyAlphanumericString,
    publicComment: validatePossiblyEmptyAlphanumericText,
    privateComment: validatePossiblyEmptyAlphanumericText,
});

const INITIAL_VALIDITY = Object.freeze(
    VALIDATED_FIELDS.reduce(
        (result, field) => ({
            ...result,
            [field]: [
                "birthDate",
                "businessName",
                "publicComment",
                "privateComment",
            ].includes(field),
        }),
        {},
    ),
);

function isBlank(value) {
    return typeof value !== "string" || value.trim().length === 0;
}

function isRequiredField(field, saleType) {
    return REQUIRED_FIELDS.includes(field)
        || (
            field === "businessName"
            && saleType === SALE_TYPE.B2B
        );
}

function isValidIsoDate(value) {
    if (isBlank(value)) {
        return true;
    }

    if (!ISO_DATE_REGEX.test(value)) {
        return false;
    }

    const [year, month, day] = value
        .split("-")
        .map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return date.getUTCFullYear() === year
        && date.getUTCMonth() === month - 1
        && date.getUTCDate() === day;
}

async function readResponsePayload(
    request,
    errorMessage,
) {
    const response = await request;

    if (
        !response
        || typeof response.text !== "function"
        || typeof response.ok !== "boolean"
    ) {
        return response;
    }

    const responseText = await response.text();
    let payload = null;

    if (responseText !== "") {
        try {
            payload = JSON.parse(responseText);
        } catch {
            payload = responseText;
        }
    }

    if (!response.ok) {
        const details =
            typeof payload === "string"
                ? payload
                : payload == null
                    ? ""
                    : JSON.stringify(payload);

        throw new Error(
            `${errorMessage} HTTP ${response.status}`
            + (details ? `: ${details}` : ""),
        );
    }

    return payload;
}

function extractCollection(
    payload,
    preferredKeys = [],
    itemIdKeys = [],
) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (payload == null || typeof payload !== "object") {
        return [];
    }

    if (
        itemIdKeys.some(
            (key) => payload[key] != null,
        )
    ) {
        return [payload];
    }

    const collectionKeys = [
        ...preferredKeys,
        "content",
        "items",
        "results",
        "values",
        "data",
    ];

    for (const key of collectionKeys) {
        if (!(key in payload)) {
            continue;
        }

        const value = payload[key];

        if (Array.isArray(value)) {
            return value;
        }

        const nestedCollection = extractCollection(
            value,
            [],
            itemIdKeys,
        );

        if (nestedCollection.length > 0) {
            return nestedCollection;
        }
    }

    return Object.values(payload).find(Array.isArray) ?? [];
}

function extractBoolean(payload) {
    if (typeof payload === "boolean") {
        return payload;
    }

    if (typeof payload === "number") {
        return payload !== 0;
    }

    if (typeof payload === "string") {
        const normalizedValue = payload.trim().toLowerCase();

        if (normalizedValue === "true") {
            return true;
        }

        if (normalizedValue === "false") {
            return false;
        }
    }

    if (payload && typeof payload === "object") {
        for (const key of [
            "value",
            "result",
            "valid",
            "success",
        ]) {
            if (typeof payload[key] === "boolean") {
                return payload[key];
            }
        }
    }

    return false;
}

async function validationToPromise(
    request,
    showMessage,
) {
    try {
        const payload = await readResponsePayload(
            request,
            LABEL.validationError,
        );

        return extractBoolean(payload);
    } catch (error) {
        console.error(LABEL.validationError, error);
        showMessage?.(
            error?.message || LABEL.validationError,
        );
        return false;
    }
}

function normalizeCategory(category) {
    if (
        typeof category === "number"
        || typeof category === "string"
    ) {
        return {
            id: category,
            name: `Kategorie ${category}`,
        };
    }

    if (!category || typeof category !== "object") {
        return null;
    }

    const id = category.categoryId ?? category.id;

    if (id == null || id === "") {
        return null;
    }

    return {
        id,
        name:
            category.categoryName
            ?? category.name
            ?? category.title
            ?? category.label
            ?? category.description
            ?? `Kategorie ${id}`,
    };
}

function normalizeTag(tag) {
    if (
        typeof tag === "number"
        || typeof tag === "string"
    ) {
        return {
            id: String(tag),
            name: `Tag ${tag}`,
        };
    }

    if (!tag || typeof tag !== "object") {
        return null;
    }

    const id = tag.tagId ?? tag.id;

    if (id == null || id === "") {
        return null;
    }

    return {
        id: String(id),
        name:
            tag.tagName
            ?? tag.name
            ?? tag.title
            ?? tag.label
            ?? tag.description
            ?? tag.value
            ?? `Tag ${id}`,
    };
}

function normalizeInitialText(value) {
    return String(
        toEmptyStringIfNull(value ?? null),
    );
}

function createInitialValues({
    initialBusinessName,
    initialFirstName,
    initialLastName,
    initialBirthDate,
    initialPhoneNumber,
    initialEmailAddress,
    initialStreet,
    initialHouseNo,
    initialZipCode,
    initialTown,
}) {
    return {
        ...INITIAL_VALUES,
        businessName: normalizeInitialText(initialBusinessName),
        firstName: normalizeInitialText(initialFirstName),
        lastName: normalizeInitialText(initialLastName),
        birthDate: normalizeInitialText(initialBirthDate),
        phoneNumber: normalizeInitialText(initialPhoneNumber),
        emailAddress: normalizeInitialText(initialEmailAddress),
        street: normalizeInitialText(initialStreet),
        houseNo: normalizeInitialText(initialHouseNo),
        zipCode: normalizeInitialText(initialZipCode),
        town: normalizeInitialText(initialTown),
    };
}

function useLatestRef(value) {
    const ref = useRef(value);

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref;
}

function useSaleOptions({
    open,
    username,
    accessToken,
    showMessageRef,
}) {
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categoryLoadFailed, setCategoryLoadFailed] = useState(false);
    const [tagLoadFailed, setTagLoadFailed] = useState(false);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        let active = true;

        const loadOptions = async () => {
            if (isBlank(accessToken)) {
                setCategoryLoadFailed(true);
                setTagLoadFailed(true);
                showMessageRef.current?.(
                    LABEL.accessTokenMissing,
                );
                return;
            }

            setLoading(true);
            setCategoryLoadFailed(false);
            setTagLoadFailed(false);

            const [categoryResult, tagResult] =
                await Promise.allSettled([
                    readResponsePayload(
                        fetchCategoriesAPI(
                            username,
                            accessToken,
                        ),
                        LABEL.categoryLoadError,
                    ),
                    readResponsePayload(
                        fetchSaleTagsAPI(accessToken),
                        LABEL.tagLoadError,
                    ),
                ]);

            if (!active) {
                return;
            }

            if (categoryResult.status === "fulfilled") {
                const normalizedCategories = extractCollection(
                    categoryResult.value,
                    ["categories", "categoryList"],
                    ["categoryId", "id"],
                )
                    .map(normalizeCategory)
                    .filter(Boolean);

                setCategories(normalizedCategories);
                setCategoryLoadFailed(
                    normalizedCategories.length === 0,
                );
            } else {
                console.error(
                    LABEL.categoryLoadError,
                    categoryResult.reason,
                );
                setCategories([]);
                setCategoryLoadFailed(true);
                showMessageRef.current?.(
                    LABEL.categoryLoadError,
                );
            }

            if (tagResult.status === "fulfilled") {
                const normalizedTags = extractCollection(
                    tagResult.value,
                    ["tags", "tagList"],
                    ["tagId", "id"],
                )
                    .map(normalizeTag)
                    .filter(Boolean);

                setTags(normalizedTags);
                setTagLoadFailed(false);
            } else {
                console.error(
                    LABEL.tagLoadError,
                    tagResult.reason,
                );
                setTags([]);
                setTagLoadFailed(true);
                showMessageRef.current?.(
                    LABEL.tagLoadError,
                );
            }

            setLoading(false);
        };

        void loadOptions();

        return () => {
            active = false;
        };
    }, [
        accessToken,
        open,
        showMessageRef,
        username,
    ]);

    return {
        categories,
        tags,
        loading,
        categoryLoadFailed,
        tagLoadFailed,
    };
}

function useLeadFormValidation({
    values,
    setValues,
    showMessageRef,
}) {
    const [validity, setValidity] = useState(INITIAL_VALIDITY);
    const [touched, setTouched] = useState({});
    const validationSequenceRef = useRef({});
    const validationTimersRef = useRef({});
    const valuesRef = useLatestRef(values);

    const setFieldValidity = useCallback((field, value) => {
        setValidity((current) => ({
            ...current,
            [field]: Boolean(value),
        }));
    }, []);

    const markTouched = useCallback((field) => {
        setTouched((current) => ({
            ...current,
            [field]: true,
        }));
    }, []);

    const validateField = useCallback(
        async (
            field,
            value,
            saleType = valuesRef.current.saleType,
        ) => {
            const required = isRequiredField(
                field,
                saleType,
            );

            if (
                field === "businessName"
                && saleType !== SALE_TYPE.B2B
            ) {
                setFieldValidity(field, true);
                return true;
            }

            if (isBlank(value)) {
                const emptyValueIsValid = !required;
                setFieldValidity(
                    field,
                    emptyValueIsValid,
                );
                return emptyValueIsValid;
            }

            if (field === "birthDate") {
                const dateIsValid = isValidIsoDate(value);
                setFieldValidity(field, dateIsValid);
                return dateIsValid;
            }

            const validator = REMOTE_VALIDATORS[field];

            if (!validator) {
                setFieldValidity(field, true);
                return true;
            }

            const sequence =
                (validationSequenceRef.current[field] ?? 0)
                + 1;
            validationSequenceRef.current[field] = sequence;

            const fieldIsValid = await validationToPromise(
                validator(value),
                showMessageRef.current,
            );

            if (
                validationSequenceRef.current[field]
                === sequence
            ) {
                setFieldValidity(field, fieldIsValid);
            }

            return fieldIsValid;
        },
        [
            setFieldValidity,
            showMessageRef,
            valuesRef,
        ],
    );

    const scheduleValidation = useCallback(
        (field, value, saleType) => {
            clearTimeout(
                validationTimersRef.current[field],
            );

            validationTimersRef.current[field] = setTimeout(
                () => {
                    void validateField(
                        field,
                        value,
                        saleType,
                    );
                },
                VALIDATION.validationDelay ?? 400,
            );
        },
        [validateField],
    );

    const setField = useCallback(
        (field, value) => {
            setValues((current) => ({
                ...current,
                [field]: value,
            }));
            markTouched(field);
            scheduleValidation(
                field,
                value,
                values.saleType,
            );
        },
        [
            markTouched,
            scheduleValidation,
            setValues,
            values.saleType,
        ],
    );

    const blurField = useCallback(
        (field) => {
            markTouched(field);
            clearTimeout(
                validationTimersRef.current[field],
            );
            void validateField(
                field,
                valuesRef.current[field],
                valuesRef.current.saleType,
            );
        },
        [markTouched, validateField, valuesRef],
    );

    const validateAll = useCallback(async () => {
        const currentValues = valuesRef.current;
        const entries = await Promise.all(
            VALIDATED_FIELDS.map(async (field) => [
                field,
                await validateField(
                    field,
                    currentValues[field],
                    currentValues.saleType,
                ),
            ]),
        );
        const checks = Object.fromEntries(entries);

        setValidity(checks);
        setTouched(
            VALIDATED_FIELDS.reduce(
                (result, field) => ({
                    ...result,
                    [field]: true,
                }),
                {},
            ),
        );

        return Object.values(checks).every(Boolean);
    }, [validateField, valuesRef]);

    const resetValidation = useCallback(() => {
        Object.values(validationTimersRef.current)
            .forEach(clearTimeout);
        validationTimersRef.current = {};
        validationSequenceRef.current = {};
        setValidity(INITIAL_VALIDITY);
        setTouched({});
    }, []);

    useEffect(
        () => () => {
            Object.values(validationTimersRef.current)
                .forEach(clearTimeout);
        },
        [],
    );

    const getFieldProps = useCallback(
        (field) => {
            const required = isRequiredField(
                field,
                values.saleType,
            );
            const error = Boolean(
                touched[field]
                && !validity[field],
            );

            return {
                value: values[field],
                required,
                error,
                helperText: error
                    ? (
                        isBlank(values[field])
                            ? LABEL.requiredField
                            : LABEL.invalidField
                    )
                    : undefined,
                sx: FORM_FIELD_SX,
                fullWidth: true,
                onChange: (event) => {
                    setField(field, event.target.value);
                },
                onBlur: () => {
                    blurField(field);
                },
            };
        },
        [
            blurField,
            setField,
            touched,
            validity,
            values,
        ],
    );

    return {
        validity,
        touched,
        setField,
        blurField,
        validateField,
        validateAll,
        resetValidation,
        getFieldProps,
    };
}

function useLeadForm({
    open,
    businessId,
    initialValues,
    showMessageRef,
}) {
    const [values, setValues] = useState(INITIAL_VALUES);
    const {
        validity,
        touched,
        setField,
        blurField,
        validateField,
        validateAll,
        resetValidation,
        getFieldProps,
    } = useLeadFormValidation({
        values,
        setValues,
        showMessageRef,
    });

    const initialize = useCallback(
        (nextValues) => {
            setValues(nextValues);
            resetValidation();

            VALIDATED_FIELDS.forEach((field) => {
                if (!isBlank(nextValues[field])) {
                    void validateField(
                        field,
                        nextValues[field],
                        nextValues.saleType,
                    );
                }
            });
        },
        [resetValidation, validateField],
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        initialize(initialValues);
    }, [
        businessId,
        initialValues,
        initialize,
        open,
    ]);

    const reset = useCallback(() => {
        setValues(INITIAL_VALUES);
        resetValidation();
    }, [resetValidation]);

    const setControlValue = useCallback(
        (field, value) => {
            setValues((current) => ({
                ...current,
                [field]: value,
            }));
        },
        [],
    );

    const setSaleType = useCallback(
        (saleType) => {
            setValues((current) => ({
                ...current,
                saleType,
            }));

            void validateField(
                "businessName",
                values.businessName,
                saleType,
            );
        },
        [validateField, values.businessName],
    );

    const setPrice = useCallback(
        (value) => {
            if (INTEGER_REGEX.test(value)) {
                setControlValue("price", value);
            }
        },
        [setControlValue],
    );

    const setSelectedTagIds = useCallback(
        (value) => {
            setControlValue(
                "selectedTagIds",
                typeof value === "string"
                    ? value.split(",")
                    : value,
            );
        },
        [setControlValue],
    );

    return {
        values,
        reset,
        setControlValue,
        setSaleType,
        setPrice,
        setSelectedTagIds,
        validity,
        touched,
        setField,
        blurField,
        validateField,
        validateAll,
        resetValidation,
        getFieldProps,
    };
}

function useAddLeadController({
    appAPI,
    contextAPI,
    reload,
    open,
    setOpen,
    businessId,
    firm: initialBusinessName,
    firstName: initialFirstName,
    lastName: initialLastName,
    birthDate: initialBirthDate,
    phoneNo: initialPhoneNumber,
    emailAddress: initialEmailAddress,
    street: initialStreet,
    houseNo: initialHouseNo,
    zipCode: initialZipCode,
    town: initialTown,
}) {
    const [saving, setSaving] = useState(false);
    const showMessageRef = useLatestRef(appAPI.showMessage);
    const accessToken = contextAPI.password;

    const initialValues = useMemo(
        () => createInitialValues({
            initialBusinessName,
            initialFirstName,
            initialLastName,
            initialBirthDate,
            initialPhoneNumber,
            initialEmailAddress,
            initialStreet,
            initialHouseNo,
            initialZipCode,
            initialTown,
        }),
        [
            initialBirthDate,
            initialBusinessName,
            initialEmailAddress,
            initialFirstName,
            initialHouseNo,
            initialLastName,
            initialPhoneNumber,
            initialStreet,
            initialTown,
            initialZipCode,
        ],
    );

    const options = useSaleOptions({
        open,
        username: contextAPI.username,
        accessToken,
        showMessageRef,
    });

    const {
        values,
        validity,
        reset,
        validateAll,
        setControlValue,
        setSaleType,
        setPrice,
        setSelectedTagIds,
        getFieldProps,
    } = useLeadForm({
        open,
        businessId,
        initialValues,
        showMessageRef,
    });

    const tagById = useMemo(
        () => new Map(
            options.tags.map((tag) => [
                tag.id,
                tag,
            ]),
        ),
        [options.tags],
    );

    const hasValidControlValues = useMemo(() => {
        const sellerId = Number(contextAPI.id);
        const numericPrice = Number(values.price);

        return !isBlank(accessToken)
            && Number.isSafeInteger(sellerId)
            && sellerId > 0
            && values.categoryId !== EMPTY_STRING
            && Number.isSafeInteger(numericPrice)
            && numericPrice >= MIN_PRICE;
    }, [
        accessToken,
        contextAPI.id,
        values.categoryId,
        values.price,
    ]);

    const hasValidRequiredFields = useMemo(
        () => VALIDATED_FIELDS.every((field) => {
            const required = isRequiredField(
                field,
                values.saleType,
            );

            if (
                field === FIELD.businessName
                && values.saleType !== SALE_TYPE.B2B
            ) {
                return true;
            }

            if (!required && isBlank(values[field])) {
                return true;
            }

            return !isBlank(values[field])
                && validity[field];
        }),
        [validity, values],
    );

    const isFormValid =
        hasValidControlValues
        && hasValidRequiredFields
        && !options.categoryLoadFailed;

    const close = useCallback(() => {
        reset();
        setOpen(false);
    }, [reset, setOpen]);

    const save = useCallback(async () => {
        const fieldsAreValid = await validateAll();
        const sellerId = Number(contextAPI.id);
        const controlsAreValid =
            !isBlank(accessToken)
            && Number.isSafeInteger(sellerId)
            && sellerId > 0
            && values.categoryId !== EMPTY_STRING
            && Number.isSafeInteger(
                Number(values.price),
            )
            && Number(values.price) >= MIN_PRICE;

        if (!fieldsAreValid || !controlsAreValid) {
            showMessageRef.current?.(
                LABEL.invalidForm,
            );
            return;
        }

        setSaving(true);

        try {
            const saleRequest = new SaleCreationRequest({
                sellerId: contextAPI.id,
                mode: values.mode,
                price: Number(values.price),
                tags: values.selectedTagIds.map(Number),
                type: values.saleType,
                partner: {
                    businessName:
                        values.saleType === SALE_TYPE.B2B
                            ? values.businessName
                            : null,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    birthDate:
                        values.birthDate || null,
                    phoneNumber: values.phoneNumber,
                    emailAddress: values.emailAddress,
                },
                address: {
                    street: values.street,
                    houseNo: values.houseNo,
                    zipCode: values.zipCode,
                    town: values.town,
                },
                categoryId: values.categoryId,
                comments: {
                    publicComment:
                        values.publicComment,
                    privateComment:
                        values.privateComment,
                },
            });

            await readResponsePayload(
                createSaleAPI(
                    accessToken,
                    saleRequest,
                ),
                LABEL.saveError,
            );

            reload?.();
            close();
            showMessageRef.current?.(LABEL.saved);
        } catch (error) {
            console.error(LABEL.saveError, error);
            showMessageRef.current?.(
                error?.message || LABEL.saveError,
            );
        } finally {
            setSaving(false);
        }
    }, [
        accessToken,
        close,
        contextAPI.id,
        reload,
        validateAll,
        values,
        showMessageRef,
    ]);

    const onCategoryChange = useCallback(
        (event) => {
            setControlValue(
                FIELD.categoryId,
                event.target.value,
            );
        },
        [setControlValue],
    );

    const onSaleTypeChange = useCallback(
        (event) => {
            setSaleType(event.target.value);
        },
        [setSaleType],
    );

    const onModeChange = useCallback(
        (event) => {
            setControlValue(
                FIELD.mode,
                event.target.value,
            );
        },
        [setControlValue],
    );

    const onPriceChange = useCallback(
        (event) => {
            setPrice(event.target.value);
        },
        [setPrice],
    );

    const onTagsChange = useCallback(
        (event) => {
            setSelectedTagIds(
                event.target.value,
            );
        },
        [setSelectedTagIds],
    );

    const categoryItems = useMemo(
        () => options.categories.length > 0
            ? options.categories.map((category) => ({
                id: category.id,
                value: category.id,
                label: category.name,
                disabled: false,
            }))
            : [{
                id: OPTION_ID.noCategories,
                value: EMPTY_STRING,
                label: LABEL.noCategories,
                disabled: true,
            }],
        [options.categories],
    );

    const tagItems = useMemo(
        () => options.tags.length > 0
            ? options.tags.map((tag) => ({
                id: tag.id,
                value: tag.id,
                label: tag.name,
                checked: values.selectedTagIds.includes(tag.id),
                disabled: false,
                showCheckbox: true,
            }))
            : [{
                id: OPTION_ID.noTags,
                value: EMPTY_STRING,
                label: LABEL.noTags,
                checked: false,
                disabled: true,
                showCheckbox: false,
            }],
        [options.tags, values.selectedTagIds],
    );

    const selectedTags = useMemo(
        () => values.selectedTagIds.map((tagId) => {
            const normalizedTagId = String(tagId);

            return {
                id: normalizedTagId,
                label:
                    tagById.get(normalizedTagId)?.name
                    ?? normalizedTagId,
            };
        }),
        [tagById, values.selectedTagIds],
    );

    const statusAlerts = useMemo(
        () => [
            options.categoryLoadFailed
                ? {
                    id: STATUS_ID.category,
                    severity: COMPONENT_VALUE.error,
                    message: LABEL.categoryLoadError,
                }
                : null,
            options.tagLoadFailed
                ? {
                    id: STATUS_ID.tags,
                    severity: COMPONENT_VALUE.error,
                    message: LABEL.tagLoadError,
                }
                : null,
        ].filter(Boolean),
        [
            options.categoryLoadFailed,
            options.tagLoadFailed,
        ],
    );

    const configurationFields = useMemo(
        () => ({
            category: {
                fullWidth: true,
                required: true,
                disabled:
                    options.loading
                    || options.categoryLoadFailed,
                sx: FORM_FIELD_SX,
                label: LABEL.category,
                value: values.categoryId,
                menuProps: SELECT_MENU_PROPS,
                items: categoryItems,
                onChange: onCategoryChange,
            },
            saleType: {
                fullWidth: true,
                required: true,
                sx: FORM_FIELD_SX,
                label: LABEL.saleType,
                value: values.saleType,
                menuProps: SELECT_MENU_PROPS,
                items: [
                    {
                        id: SALE_TYPE.B2B,
                        value: SALE_TYPE.B2B,
                        label: LABEL.b2b,
                        disabled: false,
                    },
                    {
                        id: SALE_TYPE.B2C,
                        value: SALE_TYPE.B2C,
                        label: LABEL.b2c,
                        disabled: false,
                    },
                ],
                onChange: onSaleTypeChange,
            },
            mode: {
                fullWidth: true,
                required: true,
                sx: FORM_FIELD_SX,
                label: LABEL.mode,
                value: values.mode,
                menuProps: SELECT_MENU_PROPS,
                items: [
                    {
                        id: SALE_MODE.OFFER,
                        value: SALE_MODE.OFFER,
                        label: LABEL.offer,
                        disabled: false,
                    },
                    {
                        id: SALE_MODE.AUCTION,
                        value: SALE_MODE.AUCTION,
                        label: LABEL.auction,
                        disabled: false,
                    },
                ],
                onChange: onModeChange,
            },
            price: {
                fullWidth: true,
                required: true,
                sx: FORM_FIELD_SX,
                label:
                    values.mode === SALE_MODE.AUCTION
                        ? LABEL.minimumBid
                        : LABEL.price,
                value: values.price,
                inputProps: INPUT_PROPS.numeric,
                onChange: onPriceChange,
            },
            tags: {
                fullWidth: true,
                disabled:
                    options.loading
                    || options.tagLoadFailed,
                sx: FORM_FIELD_SX,
                label: LABEL.tags,
                value: values.selectedTagIds,
                menuProps: SELECT_MENU_PROPS,
                items: tagItems,
                selectedItems: selectedTags,
                emptyLabel: LABEL.publicSale,
                helperText: LABEL.tagsHelp,
                onChange: onTagsChange,
            },
        }),
        [
            categoryItems,
            onCategoryChange,
            onModeChange,
            onPriceChange,
            onSaleTypeChange,
            onTagsChange,
            options.categoryLoadFailed,
            options.loading,
            options.tagLoadFailed,
            selectedTags,
            tagItems,
            values.categoryId,
            values.mode,
            values.price,
            values.saleType,
            values.selectedTagIds,
        ],
    );

    const contactFields = useMemo(
        () => ({
            businessName: {
                label: LABEL.businessField,
                autoComplete: AUTO_COMPLETE.organization,
                ...getFieldProps(FIELD.businessName),
            },
            firstName: {
                label: LABEL.firstName,
                autoComplete: AUTO_COMPLETE.givenName,
                ...getFieldProps(FIELD.firstName),
            },
            lastName: {
                label: LABEL.lastName,
                autoComplete: AUTO_COMPLETE.familyName,
                ...getFieldProps(FIELD.lastName),
            },
            phoneNumber: {
                type: INPUT_TYPE.tel,
                label: LABEL.phoneNumber,
                autoComplete: AUTO_COMPLETE.tel,
                inputProps: INPUT_PROPS.tel,
                ...getFieldProps(FIELD.phoneNumber),
            },
            emailAddress: {
                type: INPUT_TYPE.email,
                label: LABEL.emailAddress,
                autoComplete: AUTO_COMPLETE.email,
                ...getFieldProps(FIELD.emailAddress),
            },
            birthDate: {
                type: INPUT_TYPE.date,
                label: `${LABEL.birthDate} ${LABEL.optional}`,
                InputLabelProps: INPUT_LABEL_PROPS,
                ...getFieldProps(FIELD.birthDate),
            },
            street: {
                label: LABEL.street,
                autoComplete: AUTO_COMPLETE.streetAddress,
                ...getFieldProps(FIELD.street),
            },
            houseNo: {
                label: LABEL.houseNo,
                ...getFieldProps(FIELD.houseNo),
            },
            zipCode: {
                label: LABEL.zipCode,
                autoComplete: AUTO_COMPLETE.postalCode,
                ...getFieldProps(FIELD.zipCode),
            },
            town: {
                label: LABEL.town,
                autoComplete: AUTO_COMPLETE.addressLevel2,
                ...getFieldProps(FIELD.town),
            },
            country: {
                fullWidth: true,
                disabled: true,
                label: LABEL.country,
                value: COUNTRY_GERMANY,
                sx: FORM_FIELD_SX,
            },
        }),
        [getFieldProps],
    );

    const commentSections = useMemo(
        () => [
            {
                id: FIELD.publicComment,
                title: LABEL.publicCommentSectionTitle,
                subtitle: LABEL.optional,
                icon: SECTION_ICONS.publicComment,
                field: {
                    multiline: true,
                    minRows: 4,
                    maxRows: 7,
                    ...getFieldProps(FIELD.publicComment),
                },
            },
            {
                id: FIELD.privateComment,
                title: LABEL.privateCommentSectionTitle,
                subtitle: LABEL.optional,
                icon: SECTION_ICONS.privateComment,
                field: {
                    multiline: true,
                    minRows: 4,
                    maxRows: 7,
                    ...getFieldProps(FIELD.privateComment),
                },
            },
        ],
        [getFieldProps],
    );

    const isBusinessLead = values.saleType === SALE_TYPE.B2B;

    return {
        dialog: {
            open,
            onClose: close,
        },
        toolbar: {
            title: LABEL.title,
            closeAriaLabel: LABEL.closeDialog,
            saveLabel: LABEL.save,
            savingLabel: LABEL.saving,
            onClose: close,
            onSave: save,
            saving,
            saveDisabled:
                !isFormValid
                || saving
                || options.loading,
        },
        status: {
            visible:
                statusAlerts.length > 0
                || options.loading,
            alerts: statusAlerts,
            loading: options.loading,
            loadingLabel: LABEL.loading,
        },
        configuration: {
            title: LABEL.offerSectionTitle,
            subtitle: LABEL.offerSectionSubtitle,
            icon: SECTION_ICONS.sale,
            fields: configurationFields,
        },
        contact: {
            title: isBusinessLead
                ? LABEL.companySectionTitle
                : LABEL.personSectionTitle,
            subtitle: LABEL.contactSectionSubtitle,
            icon: SECTION_ICONS.contact,
            showBusinessName: isBusinessLead,
            fields: contactFields,
        },
        visibility: {
            message: LABEL.visibilityWarning,
        },
        commentSections,
    };
}

const SECTION_ICONS = Object.freeze({
    sale: SellOutlinedIcon,
    contact: PersonOutlineIcon,
    publicComment: PublicOutlinedIcon,
    privateComment: LockOutlinedIcon,
});

const STYLES = Object.freeze({
    dialog: {
        zIndex: DIALOG_Z_INDEX,
    },
    dialogPaper: {
        overflow: "hidden",
        backgroundColor: "#f5f7f9",
    },
    appBar: {
        color: "common.white",
        backgroundColor: PRIMARY_COLOR,
        borderBottom: "1px solid",
        borderColor: alpha("#ffffff", 0.16),
        boxShadow: `0 3px 12px ${alpha("#0f172a", 0.12)}`,
    },
    toolbar: {
        width: "100%",
        minHeight: 72,
        px: {
            xs: 2,
            sm: 3,
            md: 4,
        },
        boxSizing: "border-box",
    },
    dialogTitle: {
        flexGrow: 1,
        minWidth: 0,
        ml: 1,
    },
    saveButton: {
        minWidth: {
            xs: 44,
            sm: 120,
        },
        height: 40,
        borderRadius: 2,
        px: {
            xs: 1.25,
            sm: 2,
        },
        color: PRIMARY_COLOR,
        backgroundColor: "common.white",
        boxShadow: "none",
        "&:hover": {
            backgroundColor: alpha("#ffffff", 0.92),
            boxShadow: "none",
        },
        "&.Mui-disabled": {
            color: alpha("#ffffff", 0.46),
            backgroundColor: alpha("#ffffff", 0.12),
        },
        "& .MuiButton-startIcon": {
            mr: {
                xs: 0,
                sm: 1,
            },
        },
    },
    saveButtonLabel: {
        display: {
            xs: "none",
            sm: "inline",
        },
    },
    dialogContent: {
        p: 0,
        maxWidth: 1200, 
        mx: "auto",
        backgroundColor: "#f5f7f9",
    },
    content: {
        width: "100%",
        maxWidth: 1120,
        px: {
            xs: 2,
            sm: 3,
            md: 4,
        },
        py: {
            xs: 3.5,
            sm: 5,
        },
        boxSizing: "border-box",
    },
    card: {
        borderRadius: 3,
        borderColor: "rgba(15, 23, 42, 0.10)",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.055)",
        backgroundColor: "background.paper",
        p: {
            xs: 2.25,
            sm: 3,
            md: 3.5,
        },
    },
    cardIcon: {
        display: "grid",
        placeItems: "center",
        flex: "0 0 auto",
        width: 40,
        height: 40,
        borderRadius: 2,
        color: PRIMARY_COLOR,
        backgroundColor: alpha(PRIMARY_COLOR, 0.08),
    },
    cardText: {
        minWidth: 0,
    },
    cardContent: {
        width: "100%",
        minWidth: 0,
    },
    cardTitle: {
        letterSpacing: "-0.01em",
        fontSize: "1rem",
        fontWeight: 700,
        color: "text.primary",
        lineHeight: 1.35,
    },
    cardSubtitle: {
        color: "text.secondary",
        fontSize: "0.875rem",
        lineHeight: 1.5,
    },
    loadingPaper: {
        px: 2,
        py: 1.5,
        borderRadius: 2,
    },
    tagValue: {
        display: "flex",
        gap: 0.5,
        flexWrap: "wrap",
        py: 0.125,
    },
    tagChip: {
        height: 24,
        borderRadius: 1.5,
    },
    addressColumn: {
        pl: {
            md: 4.5,
        },
        borderLeft: {
            md: "1px solid",
        },
        borderColor: {
            md: "rgba(15, 23, 42, 0.10)",
        },
    },
    visibilityAlert: {
        py: 0.5,
        borderRadius: 2,
        alignItems: "center",
        "& .MuiAlert-message": {
            py: 0.5,
            fontSize: "0.875rem",
            lineHeight: 1.5,
        },
        backgroundColor: "white",
    },
});

function FormCard({
    title,
    subtitle,
    icon: Icon,
    children,
}) {
    return (
        <Paper
            variant={COMPONENT_VALUE.outlined}
            sx={STYLES.card}
        >
            <Stack spacing={{ xs: 2.75, sm: 3.25 }}>
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems={COMPONENT_VALUE.flexStart}
                >
                    <Box sx={STYLES.cardIcon}>
                        <Icon fontSize={COMPONENT_VALUE.small} />
                    </Box>

                    <Box sx={STYLES.cardText}>
                        <Typography sx={STYLES.cardTitle}>
                            {title}
                        </Typography>

                        {subtitle && (
                            <Typography sx={STYLES.cardSubtitle}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <Box sx={STYLES.cardContent}>
                    {children}
                </Box>
            </Stack>
        </Paper>
    );
}

function LeadDialogAppBar({
    title,
    closeAriaLabel,
    saveLabel,
    savingLabel,
    onClose,
    onSave,
    saving,
    saveDisabled,
}) {
    return (
        <AppBar
            position={COMPONENT_VALUE.relative}
            sx={STYLES.appBar}
        >
            <Toolbar sx={STYLES.toolbar}>
                <IconButton
                    edge={COMPONENT_VALUE.start}
                    color={COMPONENT_VALUE.inherit}
                    onClick={onClose}
                    disabled={saving}
                    aria-label={closeAriaLabel}
                >
                    <CloseIcon />
                </IconButton>

                <Typography
                    variant={COMPONENT_VALUE.h6}
                    component={COMPONENT_VALUE.div}
                    noWrap
                    sx={STYLES.dialogTitle}
                >
                    {title}
                </Typography>

                <Button
                    color={COMPONENT_VALUE.inherit}
                    onClick={onSave}
                    disabled={saveDisabled}
                    startIcon={
                        saving
                            ? (
                                <CircularProgress
                                    size={16}
                                    color={COMPONENT_VALUE.inherit}
                                />
                            )
                            : <SaveOutlinedIcon />
                    }
                    sx={STYLES.saveButton}
                >
                    <Box
                        component={COMPONENT_VALUE.span}
                        sx={STYLES.saveButtonLabel}
                    >
                        {saving ? savingLabel : saveLabel}
                    </Box>
                </Button>
            </Toolbar>
        </AppBar>
    );
}

function LoadStatus({
    alerts,
    loading,
    loadingLabel,
}) {
    if (alerts.length === 0 && !loading) {
        return null;
    }

    return (
        <Stack spacing={1.25}>
            {alerts.map((alert) => (
                <Alert severity={alert.severity} key={alert.id}>
                    {alert.message}
                </Alert>
            ))}

            {loading && (
                <Paper
                    variant={COMPONENT_VALUE.outlined}
                    sx={STYLES.loadingPaper}
                >
                    <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems={COMPONENT_VALUE.center}
                    >
                        <CircularProgress size={18} />
                        <Typography
                            variant={COMPONENT_VALUE.body2}
                            color={COMPONENT_VALUE.textSecondary}
                        >
                            {loadingLabel}
                        </Typography>
                    </Stack>
                </Paper>
            )}
        </Stack>
    );
}

function SingleSelectField({ field }) {
    return (
        <FormControl
            fullWidth={field.fullWidth}
            required={field.required}
            disabled={field.disabled}
            sx={field.sx}
        >
            <InputLabel>{field.label}</InputLabel>
            <Select
                MenuProps={field.menuProps}
                value={field.value}
                label={field.label}
                onChange={field.onChange}
            >
                {field.items.map((item) => (
                    <MenuItem
                        key={item.id}
                        value={item.value}
                        disabled={item.disabled}
                    >
                        {item.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

function TagSelectionValue({
    items,
    emptyLabel,
}) {
    if (items.length === 0) {
        return (
            <Typography
                component={COMPONENT_VALUE.span}
                variant={COMPONENT_VALUE.body2}
                color={COMPONENT_VALUE.textSecondary}
            >
                {emptyLabel}
            </Typography>
        );
    }

    return (
        <Box sx={STYLES.tagValue}>
            {items.map((item) => (
                <Chip
                    key={item.id}
                    size={COMPONENT_VALUE.small}
                    variant={COMPONENT_VALUE.outlined}
                    label={item.label}
                    sx={STYLES.tagChip}
                />
            ))}
        </Box>
    );
}

function TagSelectField({ field }) {
    return (
        <FormControl
            fullWidth={field.fullWidth}
            disabled={field.disabled}
            sx={field.sx}
        >
            <InputLabel>{field.label}</InputLabel>
            <Select
                MenuProps={field.menuProps}
                multiple
                value={field.value}
                label={field.label}
                input={<OutlinedInput label={field.label} />}
                renderValue={() => (
                    <TagSelectionValue
                        items={field.selectedItems}
                        emptyLabel={field.emptyLabel}
                    />
                )}
                onChange={field.onChange}
            >
                {field.items.map((item) => (
                    <MenuItem
                        key={item.id}
                        value={item.value}
                        disabled={item.disabled}
                    >
                        {item.showCheckbox && (
                            <Checkbox
                                size={COMPONENT_VALUE.small}
                                checked={item.checked}
                            />
                        )}
                        <ListItemText primary={item.label} />
                    </MenuItem>
                ))}
            </Select>
            <FormHelperText>
                {field.helperText}
            </FormHelperText>
        </FormControl>
    );
}

function SaleConfigurationSection({
    title,
    subtitle,
    icon,
    fields,
}) {
    return (
        <FormCard
            title={title}
            subtitle={subtitle}
            icon={icon}
        >
            <Grid container {...FORM_GRID_PROPS}>
                <Grid item xs={12} md={6}>
                    <SingleSelectField field={fields.category} />
                </Grid>

                <Grid item xs={12} md={6}>
                    <SingleSelectField field={fields.saleType} />
                </Grid>

                <Grid item xs={12} md={6}>
                    <SingleSelectField field={fields.mode} />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField {...fields.price} />
                </Grid>

                <Grid item xs={12}>
                    <TagSelectField field={fields.tags} />
                </Grid>
            </Grid>
        </FormCard>
    );
}

function ContactSection({
    title,
    subtitle,
    icon,
    showBusinessName,
    fields,
}) {
    return (
        <FormCard
            title={title}
            subtitle={subtitle}
            icon={icon}
        >
            <Grid
                container
                columnSpacing={{ xs: 0, md: 4.5 }}
                rowSpacing={{ xs: 4, md: 0 }}
                alignItems={COMPONENT_VALUE.flexStart}
            >
                <Grid item xs={12} md={6}>
                    <Grid container {...FORM_GRID_PROPS}>
                        {showBusinessName && (
                            <Grid item xs={12}>
                                <TextField {...fields.businessName} />
                            </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                            <TextField {...fields.firstName} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField {...fields.lastName} />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField {...fields.phoneNumber} />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField {...fields.emailAddress} />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField {...fields.birthDate} />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box sx={STYLES.addressColumn}>
                        <Grid container {...FORM_GRID_PROPS}>
                            <Grid item xs={12} sm={8}>
                                <TextField {...fields.street} />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField {...fields.houseNo} />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField {...fields.zipCode} />
                            </Grid>

                            <Grid item xs={12} sm={8}>
                                <TextField {...fields.town} />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField {...fields.country} />
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </FormCard>
    );
}

function CommentSection({ section }) {
    return (
        <FormCard
            title={section.title}
            subtitle={section.subtitle}
            icon={section.icon}
        >
            <TextField {...section.field} />
        </FormCard>
    );
}

function VisibilityNotice({ message }) {
    return (
        <Alert
            severity={COMPONENT_VALUE.warning}
            variant={COMPONENT_VALUE.outlined}
            sx={STYLES.visibilityAlert}
        >
            {message}
        </Alert>
    );
}

function AddLeadDialog({
    dialog,
    toolbar,
    status,
    configuration,
    contact,
    visibility,
    commentSections,
}) {
    return (
        <Dialog
            fullScreen
            open={dialog.open}
            onClose={dialog.onClose}
            sx={STYLES.dialog}
            PaperProps={{
                sx: STYLES.dialogPaper,
            }}
        >
            <LeadDialogAppBar {...toolbar} />

            <DialogContent sx={STYLES.dialogContent}>
                <Grid
                    container
                    justifyContent={COMPONENT_VALUE.center}
                >
                    <Grid item xs={12} sx={STYLES.content}>
                        <Grid
                            container
                            rowSpacing={{ xs: 4, sm: 5 }}
                        >
                            {status.visible && (
                                <Grid item xs={12}>
                                    <LoadStatus {...status} />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <SaleConfigurationSection
                                    {...configuration}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <ContactSection {...contact} />
                            </Grid>

                            <Grid item xs={12}>
                                <VisibilityNotice {...visibility} />
                            </Grid>

                            {commentSections.map((section) => (
                                <Grid item xs={12} key={section.id}>
                                    <CommentSection section={section} />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}

export default function LeadCreationDialog(props) {
    const viewModel = useAddLeadController(props);

    return <AddLeadDialog {...viewModel} />;
}